import { EmptyPage } from "@components/common/empty-page/empty-page";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { useBackgroundTasksTracker } from "@hooks/background-tasks/use-background-tasks-tracker";
import { Grid2 } from "@mui/material";
import { PROJECT_BACKGROUND_TASKS_REQUIRED_COLUMNS } from "@pages/project-details/project-bg-tasks/project-background-tasks-columns";
import { ProjectBackgroundTasksSkeleton } from "@pages/project-details/project-bg-tasks/project-background-tasks-skeleton";
import { ProjectBackgroundTasksTable } from "@pages/project-details/project-bg-tasks/project-background-tasks-table";
import { appStateSelector } from "@store/app/app-selector";
import { fetchingProjectsFlagsSelector } from "@store/projects/projects-selector";
import { sdbBackgroundTasksSelector } from "@store/sdb-background-tasks/sdb-background-tasks-selector";
import { useAppSelector } from "@store/store-helper";
import { TABS_CONTENT_PADDING } from "@utils/ui-utils";
import { filterVideoModeTasks } from "@pages/project-details/project-details";
import { NoCloudActivityDuoIcon } from "@flat-ui";

/**
 * Contains the backgrounds tasks of the project details
 */
export function ProjectBackgroundTasks({
  projectId,
}: BaseProjectIdProps): JSX.Element {
  const { isDevModeEnabled } = useAppSelector(appStateSelector);
  const taskType = isDevModeEnabled ? undefined : "VideoMode";

  const { isFetchingProjects } = useAppSelector(fetchingProjectsFlagsSelector);
  const { isLoading } = useBackgroundTasksTracker({
    projectId,
    taskType,
  });
  const sdbBackgroundsTasks = useAppSelector(sdbBackgroundTasksSelector);

  /** Filter out non-video mode tasks unless dev mode is enabled */
  const filteredSdbBackgroundTasks = filterVideoModeTasks(
    sdbBackgroundsTasks,
    isDevModeEnabled
  );

  /** Content to be shown in the page, either empty page or cloud activity table */
  let content: JSX.Element;

  const isLoadingBackgroundTasks =
    (isLoading || isFetchingProjects) &&
    filteredSdbBackgroundTasks.length === 0;

  if (isLoadingBackgroundTasks) {
    content = <ProjectBackgroundTasksSkeleton />;
  } else if (filteredSdbBackgroundTasks.length === 0) {
    // If the background activity is loaded and has no background tasks, show an empty page.
    content = (
      <EmptyPage
        title="No recorded activity"
        subtitle="There is no recorded activity for this project."
        icon={NoCloudActivityDuoIcon}
      />
    );
  } else {
    content = (
      // If the background activity is loading or has background tasks, show the table.
      // The component takes care of showing skeletons if the data is loading.
      <ProjectBackgroundTasksTable
        sdbBackgroundsTasks={filteredSdbBackgroundTasks}
        isLoading={isLoadingBackgroundTasks}
        requiredColumns={PROJECT_BACKGROUND_TASKS_REQUIRED_COLUMNS}
        projectId={projectId}
        taskType={taskType}
      />
    );
  }

  return (
    <Grid2 container sx={{ mt: TABS_CONTENT_PADDING, width: "100%" }}>
      <Grid2 width="100%">{content}</Grid2>
    </Grid2>
  );
}
